import { AddressView, Button, LoadingSpinner, Place } from "@xapp/components";

import React, { FC, useCallback, useState } from "react";
import { OnboardingProps } from "../models";

export interface AddressStepProps {
    /**
     * API Key for Google Places API
     */
    apiKey: string;
    /**
     * If network activity is happening
     */
    saving?: boolean;
    /**
     * App props passed around through the onboarding.
     */
    app?: OnboardingProps;
    /**
     * Optional style applied to the root div
     */
    style?: React.CSSProperties;
    /**
     * Optional class name to apply to the root div
     */
    className?: string;
    /**
     * User has confirmed a place with name and address.  The place will be on the new props.
     */
    onNext(createProps: OnboardingProps): void;
    /**
     * Will add an optional back button to go back a previous step
     */
    onBack?(): void;
}

export const AddressStep: FC<AddressStepProps> = (props) => {
    const { app, apiKey, onNext, onBack, saving, style, className } = props;

    const [place, setPlace] = useState<Place>({
        name: app?.name || "",
        website: app?.website || "",
        phone: app?.leadsContactPhone || "",
    });
    const [valid, setValid] = useState(false);

    const handlePlace = (place: Place, valid: boolean) => {
        setPlace(place);
        setValid(valid);
    };

    const handleNextClicked = useCallback(() => {
        onNext({
            ...app,
            place,
        });
    }, [app, onNext, place]);

    const hasGoogleBusinessProfile = app?.beta?.onboarding?.hasGoogleBusinessProfile;

    return (
        <div style={{ ...style }} className={className}>
            <div style={{ display: "flex", flexDirection: "column", rowGap: "10px", minHeight: "450px" }}>
                {saving ? (
                    <LoadingSpinner loadingText="Saving business address..." />
                ) : (
                    <>
                        {hasGoogleBusinessProfile ? (
                            <>
                                <h2>Let's find your profile</h2>
                                <p>
                                    If you can't find your business by name, please search using the address associated
                                    with your Google Business Profile.
                                </p>
                            </>
                        ) : (
                            <>
                                <h3>What is your business address?</h3>
                                <p>
                                    Make sure this matches the address you use when you setup your Google Business
                                    Profile.
                                </p>
                            </>
                        )}
                        <AddressView
                            value={place}
                            apiKey={apiKey}
                            hideMap={true}
                            tips={
                                <div style={{ fontSize: "0.9em" }}>
                                    <b>Tips</b>
                                    <ul style={{ paddingLeft: "20px" }}>
                                        <li>
                                            If you publish your address on your profile, you can search for your
                                            business name.
                                        </li>
                                        <li>
                                            If you run your business out of your home, search for your home address.
                                        </li>
                                        <li>
                                            If nothing is showing up, you can always enter your address and type it in
                                            manually.
                                        </li>
                                    </ul>
                                </div>
                            }
                            onChange={handlePlace}
                        />
                    </>
                )}
            </div>

            <div
                style={{
                    padding: "20px",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: typeof onBack === "function" ? "space-between" : "right",
                }}
            >
                {typeof onBack === "function" && (
                    <Button leftIcon="arrow_back" label={"Back"} variation="secondary" onClick={onBack} />
                )}
                <Button
                    id="stepTwoNext"
                    rightIcon="arrow_forward"
                    label={`Next`}
                    variation="primary"
                    disabled={!place || !valid}
                    onClick={handleNextClicked}
                />
            </div>
        </div>
    );
};
